import React, { useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useForm, FormProvider } from 'react-hook-form';
import PurchaseFormContext from './PurchaseFormContext';
import CheckoutPageContext from '../../../context/CheckoutPageContext';
import { getValidationFunctions } from '../../../tools/validations/formValidation';
import componentMap from '../../Steps/componentMap';

export default function PurchaseFormProvider({ children }) {
  const { checkoutPageState } = useContext(CheckoutPageContext) || {};
  const { orderContact, checkoutFormConfig, userData } = checkoutPageState || {};

  const validationFunctions = useMemo(() => {
    const validationConfig = {
      invalidEmailMessage: orderContact?.invalidEmailLabel?.value,
      invalidPhoneMessage: orderContact?.invalidPhoneLabel?.value,
    };
    return getValidationFunctions(validationConfig);
  }, [orderContact]);

  const defaultValues = useMemo(() => {
    const allFields = Object.values(componentMap).reduce((acc, Component) => {
      if (Component && typeof Component.validationFields === 'function') {
        return { ...acc, ...Component.validationFields(checkoutPageState) };
      }
      return acc;
    }, {});

    return {
      ...allFields,
      smsOptIn: false,
    };
  }, [checkoutPageState]);

  const methods = useForm({
    defaultValues,
    mode: 'onBlur',
  });

  const { reset } = methods;

  useEffect(() => {
    if (orderContact) {
      const guestCustomerMailId = sessionStorage?.getItem('guestCustomerMailId');
      const email = (guestCustomerMailId && !userData?.isLoggedIn) ? guestCustomerMailId : orderContact.email || '';

      reset((prev) => ({
        ...prev,
        addresses: {
          ...prev?.addresses,
          [orderContact.addressType]: {
            ...prev?.addresses?.[orderContact.addressType],
            email,
            phone: orderContact.phoneNumber || '',
          },
          billing: {
            ...prev?.addresses?.billing,
            email,
            phone: orderContact.phoneNumber || '',
          },
        },
        smsOptIn: false,
        ...(checkoutFormConfig?.defaultValues || {}),
      }));
    }
  }, [orderContact, checkoutFormConfig, reset, userData]);

  const contextValue = useMemo(() => ({
    methods,
    validationFunctions,
  }), [methods, validationFunctions]);

  return (
    <PurchaseFormContext.Provider value={contextValue}>
      <FormProvider {...methods}>
        {children}
      </FormProvider>
    </PurchaseFormContext.Provider>
  );
}

PurchaseFormProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
